'use client'

import { useState } from 'react'
import type { Episode } from '@/payload-types'
import { useSession } from '@/lib/auth-client'

import { Heading } from '@systhemaui/react'
import { Lexical } from '@systhemaui/payload/lexical'
import EpisodesList from './EpisodesList'

type Season = {
  label: string
  episodes: Episode[]
}

type TitleTabsProps = {
  seasons: Season[]
  trailer: Episode[]
  moreInfo: {
    root: {
      type: string
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children: any[]
      direction: ('ltr' | 'rtl') | null
      format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | ''
      indent: number
      version: number
    }
    [k: string]: unknown
  } | null
  slug: string
}

type Tab = 'episodes' | 'trailer' | 'moreInfo'

const TitleTabs: React.FC<TitleTabsProps> = ({ seasons, trailer, moreInfo, slug }) => {
  const { data: session } = useSession()
  const isSignedIn = !!session
  const hasEpisodes = seasons.length > 0 && seasons.some((s) => s.episodes.length > 0)
  const hasTrailer = trailer.length > 0
  const hasMoreInfo = moreInfo !== null

  // Determine default tab
  const getDefaultTab = (): Tab | null => {
    if (hasEpisodes) return 'episodes'
    if (hasTrailer) return 'trailer'
    if (hasMoreInfo) return 'moreInfo'
    return null
  }

  const [activeTab, setActiveTab] = useState<Tab | null>(getDefaultTab)
  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(0)

  // If no content at all, don't render anything
  if (!hasEpisodes && !hasTrailer && !hasMoreInfo) {
    return null
  }

  const tabs: { key: Tab; label: string; available: boolean }[] = [
    { key: 'episodes', label: 'Epizódok', available: hasEpisodes },
    { key: 'trailer', label: 'Trailer', available: hasTrailer },
    { key: 'moreInfo', label: 'További információk', available: hasMoreInfo },
  ]

  const availableTabs = tabs.filter((t) => t.available)
  const showSeasonSelector = activeTab === 'episodes' && seasons.length >= 1

  const currentEpisodes =
    activeTab === 'episodes' ? (seasons[selectedSeasonIndex]?.episodes ?? []) : trailer

  return (
    <section className="title-tabs w-full pt-0 pb-(--section-padding-y)">
      <div className="container flex w-full flex-col">
        {/* Tab Navigation */}
        <div className="flex w-full flex-wrap items-center border-b border-white">
          <div className="flex flex-1 flex-wrap items-center">
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`font-loos-wide px-5 py-2.5 text-(length:--button-primary-font-size) font-bold transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white text-black'
                    : 'bg-transparent text-white hover:bg-white/10'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-8 w-full">
          {activeTab === 'episodes' && (
            <div className="flex w-full flex-col">
              {/* Episodes header with season selector */}
              <div className="mb-6 flex w-full flex-wrap items-center gap-(--gap-sm)">
                <Heading.h4 className="min-w-0 flex-1">Epizódok</Heading.h4>
                {showSeasonSelector && (
                  <div className="form-select-wrapper relative">
                    <select
                      value={selectedSeasonIndex}
                      onChange={(e) => setSelectedSeasonIndex(Number(e.target.value))}
                      className="form-select min-w-50 cursor-pointer appearance-none pr-[calc(var(--form-input-block-padding-x)+var(--form-input-block-icon-size))]"
                    >
                      {seasons.map((season, index) => (
                        <option key={season.label} value={index}>
                          {season.label}
                        </option>
                      ))}
                    </select>
                    <div className="form-select-icon" />
                  </div>
                )}
              </div>
              <EpisodesList
                episodes={currentEpisodes}
                showHeader={false}
                isSignedIn={isSignedIn}
                slug={slug}
              />
            </div>
          )}

          {activeTab === 'trailer' && (
            <EpisodesList
              episodes={trailer}
              showHeader={false}
              isSignedIn={isSignedIn}
              slug={slug}
            />
          )}

          {activeTab === 'moreInfo' && moreInfo && (
            <div className="py-6">
              <Lexical data={moreInfo} />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default TitleTabs
